import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Pagination,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiClient from '../../../api/client';

interface GradingRecord {
    id: number;
    receiving_number: string;
    batch_number: string;
    po_number: string;
    grading_date: string;
    status: string;
    created_at: string;
}

const GradingQueuePage: React.FC = () => {
    const navigate = useNavigate();
    const [records, setRecords] = useState<GradingRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const limit = 20;

    useEffect(() => {
        fetchRecords();
    }, [page]);

    const fetchRecords = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/grading/queue', {
                params: { page, limit }
            });
            console.log('Grading queue API response:', response.data);
            setRecords(response.data.grading_records || []);
            setTotal(response.data.total || 0);
            setError(null);
        } catch (err: any) {
            console.error('Grading queue error:', err);
            setError(err.response?.data?.detail || 'Failed to fetch grading queue');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        const colors: Record<string, 'default' | 'primary' | 'success'> = {
            draft: 'default',
            completed: 'success',
            grn_generated: 'success'
        };
        return colors[status] || 'default';
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Grading Queue (L2)</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Receiving #</TableCell>
                                    <TableCell>Batch #</TableCell>
                                    <TableCell>PO #</TableCell>
                                    <TableCell>Grading Date</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records.map((record) => (
                                    <TableRow key={record.id} hover>
                                        <TableCell>{record.receiving_number}</TableCell>
                                        <TableCell>
                                            <Chip label={record.batch_number} size="small" color="primary" />
                                        </TableCell>
                                        <TableCell>{record.po_number}</TableCell>
                                        <TableCell>
                                            {format(new Date(record.grading_date), 'dd MMM yyyy')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={record.status.toUpperCase()}
                                                size="small"
                                                color={getStatusColor(record.status)}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/inward/grading/${record.id}`)}
                                                title="View/Edit"
                                            >
                                                {record.status === 'draft' ? <EditIcon /> : <ViewIcon />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={Math.ceil(total / limit)}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default GradingQueuePage;
